import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, catchError, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpService } from './http.service';

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {

  private isLoggedInSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  isLoggedIn$: Observable<boolean> = this.isLoggedInSubject.asObservable();
  private refreshTokenInProgress = false;
  private currentUserSubject = new BehaviorSubject<any>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private readonly _http: HttpService,
    private router: Router
  ) {
    // Do not call loadUserOnInit() here to avoid circular dependency
  }

  register(credentials: any): Observable<AuthResponse> {
    return this._http.post<any>('/auth/register', credentials).pipe(
      map((response: any) => response),
      catchError(this.handleError)
    );
  }

  login(credentials: any): Observable<AuthResponse> {
    return this._http.post<any>('/auth/login', credentials).pipe(
      tap((response: any) => {
        this.handleAuthResponse(response?.data);
        this.fetchCurrentUser().subscribe();
      }),
      catchError(this.handleError)
    );
  }

  loginWithGoogle(credential: string) {
    return this._http.post<any>('/auth/login/google', { idToken: credential }).pipe(
      tap((response: any) => {
        this.handleAuthResponse(response?.data);
        this.fetchCurrentUser().subscribe();
      }),
      catchError(this.handleError)
    );
  }

  refreshToken(): Observable<AuthResponse> {
    if (this.refreshTokenInProgress) {
      this.refreshTokenInProgress = false; // Reset the flag to allow future refresh attempts
      return throwError(() => new Error('Refresh token already in progress'));
    }

    this.refreshTokenInProgress = true;
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    return this._http.post<any>('/auth/refresh-token', { refreshToken }).pipe(
      tap((response: any) => this.handleAuthResponse(response?.data)),
      catchError(error => {
        this.logout();
        return throwError(() => error);
      }),
      tap(() => this.refreshTokenInProgress = false)
    );
  }

  resetPassword(credentials: any): Observable<any> {
    return this._http.post('/auth/password/reset', credentials).pipe(
      catchError(this.handleError)
    );
  }

  forgotPassword(email: string): Observable<any> {
    return this._http.post('/auth/password/forgot', { email }).pipe(
      map((response: any) => response?.data),
      catchError(this.handleError)
    );
  }

  resendOtp(otpId: any) {
    return this._http.post('/otp/resend', { otpId }).pipe(
      tap((response: any) => {
        console.log('OTP resent successfully:', response);
      }),
      catchError(error => {
        console.error('Failed to resend OTP:', error);
        return throwError(() => error);
      })
    );
  }

  verifyOtp(payload: any): Observable<any> {
    return this._http.post('/otp/verify', payload).pipe(
      tap((response: any) => {
        console.log('OTP verification response:', response);
      }),
      catchError(this.handleError)
    );
  }

  logout(): void {
    this._http.post('/auth/logout', {}).subscribe({
      next: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        this.isLoggedInSubject.next(false);
        this.currentUserSubject.next(null);
        this.router.navigate(['/home']);
      },
      error: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        this.isLoggedInSubject.next(false);
        this.currentUserSubject.next(null);
        this.router.navigate(['/home']);
      }
    });
  }

  private handleAuthResponse(response: AuthResponse): void {
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    this.isLoggedInSubject.next(true);
    this.setTokenExpiration(response.expiresIn);
  }

  // Public method to be called from AppComponent or similar after bootstrap
  loadUserOnInit() {
    if (this.hasValidToken()) {
      this.fetchCurrentUser().subscribe();
    }
  }

  getCurrentUser(): any {
    return this.currentUserSubject.value;
  }

  fetchCurrentUser(): Observable<any> {
    return this._http.get<any>('/auth/profile').pipe(
      tap(user => this.currentUserSubject.next(user?.data)),
      catchError(err => {
        this.currentUserSubject.next(null);
        return throwError(() => err);
      })
    );
  }

  get isAdmin$(): Observable<boolean> {
    return this.currentUser$.pipe(
      map(user => {
        if (!user || !Array.isArray(user.roles)) {
          return false;
        }
        return user.roles.includes('ADMIN');
      })
    );
  }


  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  private hasValidToken(): boolean {
    const accessToken = this.getAccessToken();
    return !!accessToken && !this.isTokenExpired();
  }

  private isTokenExpired(): boolean {
    const expiration = localStorage.getItem('tokenExpiration');
    if (!expiration) return true;

    const expiryTime = Number(expiration);
    console.log(Date.now(), expiryTime);
    return Date.now() >= expiryTime;
  }

  private setTokenExpiration(expiresIn: number): void {
    const expirationTime = Date.now() + (expiresIn * 1000); // Convert seconds to milliseconds
    localStorage.setItem('tokenExpiration', expirationTime.toString());
  }

  private handleError(error: any): Observable<never> {
    console.error('Authentication Error:', error);
    return throwError(() => error);
  }
}
